/**
 * Request-body rules for outbound webhooks (TO-381).
 *
 * Each outbound route answers one 422 code per rule broken, so these parsers
 * return a code rather than a Zod error. When a body breaks several rules the
 * code is chosen in a fixed order — shape of the body first, then the target
 * URL, then catalog membership — so the answer is deterministic.
 *
 * `tenantId` is never an input: it comes from the DB-resolved principal.
 */
import {
	isOutboundEventType,
	OUTBOUND_EVENT_CATALOG,
	OUTBOUND_TARGET_URL_MAX_LENGTH,
	OutboundDeliveryStatus,
	type OutboundEventType,
	ValidationErrorCode,
} from '@today/types'
import { z } from 'zod'

/** A parse outcome: the cleaned value, or the one 422 code that answers it. */
export type OutboundParseResult<T> = { ok: true; data: T } | { ok: false; code: ValidationErrorCode }

export interface SubscriptionCreateInput {
	targetUrl: string
	eventTypes: OutboundEventType[]
	enabled: boolean
}

export type SubscriptionUpdateInput = Partial<SubscriptionCreateInput>

const distinct = (values: readonly unknown[]) => new Set(values).size === values.length

const eventTypeListSchema = z.array(z.string()).min(1).refine(distinct)

/** The `POST /webhooks/subscriptions` body shape (also the OpenAPI request schema). */
export const outboundSubscriptionCreateSchema = z
	.object({ targetUrl: z.string(), eventTypes: eventTypeListSchema, enabled: z.boolean().optional() })
	.strict()

/** The `PATCH /webhooks/subscriptions/{id}` body shape (also the OpenAPI request schema). */
export const outboundSubscriptionUpdateSchema = z
	.object({
		targetUrl: z.string().optional(),
		eventTypes: eventTypeListSchema.optional(),
		enabled: z.boolean().optional(),
	})
	.strict()
	.refine((body) => Object.keys(body).length > 0)

// After WHATWG parsing an IPv4 host is always dotted-decimal (`0x7f.1` becomes
// `127.0.0.1`) and an IPv6 host is always bracketed.
const IPV4_HOST = /^\d{1,3}(?:\.\d{1,3}){3}$/
const INTERNAL_SUFFIXES = ['.localhost', '.local', '.internal', '.localdomain']

/**
 * Whether a partner URL meets the write-time rules (outbound-webhook-subscriptions
 * BR-1): absolute `https`, ≤ 2048 characters, no userinfo or fragment, port
 * absent or 443, and a DNS host that is neither an IP literal nor an internal
 * name. The send-time address guard re-checks what the name resolves to.
 */
export const isAcceptableTargetUrl = (raw: string): boolean => {
	if (raw.length > OUTBOUND_TARGET_URL_MAX_LENGTH || raw.includes('#')) return false
	let url: URL
	try {
		url = new URL(raw)
	} catch {
		return false
	}
	if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') return false
	if (url.port !== '' && url.port !== '443') return false
	// A trailing dot (`svc.internal.`) is the same name in DNS — strip it first.
	const host = url.hostname.toLowerCase().replace(/\.$/, '')
	if (host === '' || host === 'localhost' || host.startsWith('[') || IPV4_HOST.test(host)) return false
	return !INTERNAL_SUFFIXES.some((suffix) => host.endsWith(suffix))
}

const checkFields = (fields: { targetUrl?: string; eventTypes?: string[] }): ValidationErrorCode | null => {
	if (fields.targetUrl !== undefined && !isAcceptableTargetUrl(fields.targetUrl)) {
		return ValidationErrorCode.WEBHOOK_TARGET_URL_INVALID
	}
	if (fields.eventTypes?.some((type) => !isOutboundEventType(type))) {
		return ValidationErrorCode.WEBHOOK_EVENT_TYPE_UNKNOWN
	}
	return null
}

/** Parses a `POST /webhooks/subscriptions` body. `enabled` defaults to `true`. */
export const parseSubscriptionCreate = (body: unknown): OutboundParseResult<SubscriptionCreateInput> => {
	const shaped = outboundSubscriptionCreateSchema.safeParse(body)
	if (!shaped.success) return { ok: false, code: ValidationErrorCode.WEBHOOK_SUBSCRIPTION_INVALID }
	const code = checkFields(shaped.data)
	if (code) return { ok: false, code }
	return {
		ok: true,
		data: {
			targetUrl: shaped.data.targetUrl,
			eventTypes: shaped.data.eventTypes as OutboundEventType[],
			enabled: shaped.data.enabled ?? true,
		},
	}
}

/** Parses a `PATCH /webhooks/subscriptions/{id}` body: any non-empty subset. */
export const parseSubscriptionUpdate = (body: unknown): OutboundParseResult<SubscriptionUpdateInput> => {
	const shaped = outboundSubscriptionUpdateSchema.safeParse(body)
	if (!shaped.success) return { ok: false, code: ValidationErrorCode.WEBHOOK_SUBSCRIPTION_INVALID }
	const code = checkFields(shaped.data)
	if (code) return { ok: false, code }
	return { ok: true, data: shaped.data as SubscriptionUpdateInput }
}

/** The `PUT /webhooks/payload-shapes/{eventType}` body (also the OpenAPI request schema). */
export const payloadShapeBodySchema = z.object({ fields: z.array(z.string()).refine(distinct) }).strict()
/** The preview body (also the OpenAPI request schema). */
export const payloadShapePreviewSchema = z.object({ fields: z.array(z.string()).refine(distinct).optional() }).strict()

/** A shape parse outcome. `fields: null` means "no candidate list — use the effective shape". */
export type PayloadShapeParseResult = { ok: true; fields: string[] | null } | { ok: false; code: ValidationErrorCode }

/**
 * Parses a payload-shape `PUT` body (`fieldsRequired: true`) or preview body
 * (`fieldsRequired: false`, where an absent body or `fields` is allowed) for a
 * catalog event type. Returned fields are in the catalog's order.
 */
export const parsePayloadShapeBody = (
	eventType: OutboundEventType,
	body: unknown,
	options: { fieldsRequired: boolean },
): PayloadShapeParseResult => {
	const schema = options.fieldsRequired ? payloadShapeBodySchema : payloadShapePreviewSchema
	const shaped = schema.safeParse(body ?? (options.fieldsRequired ? undefined : {}))
	if (!shaped.success) return { ok: false, code: ValidationErrorCode.WEBHOOK_PAYLOAD_SHAPE_INVALID }
	const fields = shaped.data.fields
	if (fields === undefined) return { ok: true, fields: null }
	const available = OUTBOUND_EVENT_CATALOG[eventType].availableFields
	if (fields.some((field) => !available.includes(field))) {
		return { ok: false, code: ValidationErrorCode.WEBHOOK_PAYLOAD_FIELD_UNKNOWN }
	}
	return { ok: true, fields: available.filter((field) => fields.includes(field)) }
}

/** Delivery-log query (`filters[status]`, `page[size]`, `page[cursor]`). */
export const outboundDeliveryLogQuerySchema = z.object({
	status: z.enum(OutboundDeliveryStatus).optional(),
	pageSize: z.coerce.number().int().min(1).max(100).default(25),
	cursor: z.string().min(1).optional(),
})

export type OutboundDeliveryLogQuery = z.infer<typeof outboundDeliveryLogQuerySchema>
